import { useSyncExternalStore } from 'react'

/**
 * The FursaHub "wave" band across the top of every page, as on the website's
 * web app (Website/fursahub/components/WebApp/Shell/PageHeroBackdrop.tsx).
 * Files live in public/media: a 10s silent loop (WebM, MP4 fallback) and a
 * still that shows first and stays when the video is skipped.
 */
const WAVE = {
  webm: '/media/wave.webm',
  mp4: '/media/wave.mp4',
  still: '/media/wave-still.webp',
} as const

type NetworkInformation = { saveData?: boolean; effectiveType?: string }

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

/** No video for reduced motion, Data Saver, or a 2G/3G connection. */
function videoAllowed(): boolean {
  if (window.matchMedia(REDUCED_MOTION).matches) return false
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection
  if (connection?.saveData) return false
  return !['slow-2g', '2g', '3g'].includes(connection?.effectiveType ?? '')
}

function subscribe(onChange: () => void): () => void {
  const query = window.matchMedia(REDUCED_MOTION)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

export function PageHero() {
  return <WaveBackdrop className="fh-hero" />
}

/**
 * The wave still plus, where allowed, the looping video, filling `className`'s
 * box. Shared by the page band above and the sign-in screen's full-screen
 * backdrop; the box's own CSS sizes and fades it.
 */
export function WaveBackdrop({ className }: { className: string }) {
  const playVideo = useSyncExternalStore(subscribe, videoAllowed, () => false)

  return (
    <div className={className} aria-hidden="true">
      <img src={WAVE.still} alt="" fetchPriority="low" />
      {playVideo && (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={WAVE.still}
          tabIndex={-1}
          disablePictureInPicture
        >
          <source src={WAVE.webm} type="video/webm" />
          <source src={WAVE.mp4} type="video/mp4" />
        </video>
      )}
    </div>
  )
}
